import { useRef } from "react";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import { ActivityData, NewActivity } from "@/entities/activities/types";
import { usePeopleData } from "@/components/layouts/Activities/ActivityModal/usePeopleData";
import { useActivityModalState } from "./useActivityModalState";
import { useActivityModalData } from "./useActivityModalData";
import { useActivityModalFields } from "./useActivityModalFields";
import { useActivityModalSubmission } from "./useActivityModalSubmission";
import type { ActivitySubmissionPayload } from "./useActivityModalSubmission";
import { useActivityModalLifecycle } from "./useActivityModalLifecycle";

interface UseActivityModalParams {
  activityType: NewActivity;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

interface UseActivityModalReturn {
  selectedParticipants: string[];
  setSelectedParticipants: (participants: string[]) => void;
  isFormValid: boolean;
  setIsFormValid: (valid: boolean) => void;
  formSetValue: any;
  handleSetValue: (setValue: any) => void;
  activityData: any;
  relatedCompanyData: EntityData | undefined;
  isCompanySet: boolean;
  participantsOptions: any[];
  onParticipantsSearch: (search: string) => void;
  isLoadingParticipants: boolean;
  getBaseFields: () => Record<string, any>;
  getDateField: () => any;
  getTimeField: () => any;
  getParticipantsField: (label?: string) => any;
  getSubjectField: () => any;
  processFieldsForEditMode: (fields: any[]) => any[];
  handleSubmit: (
    data: any,
    customTransform?: (
      baseData: ActivitySubmissionPayload
    ) => ActivitySubmissionPayload
  ) => Promise<void>;
  isSubmitting: boolean;
  participantNames: string[];
  peopleEmails: any[];
  resetPeopleData: () => void;
  resetParticipantNames: () => void;
  formRef: React.RefObject<HTMLFormElement>;
  isCreatePersonModalOpen: boolean;
  setIsCreatePersonModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onPersonCreation: (personId: string, personName: string) => void;
  effectiveCompanyId: string;
}

export const useActivityModal = ({
  activityType,
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}: UseActivityModalParams): UseActivityModalReturn => {
  const formRef = useRef<HTMLFormElement>(null);

  const state = useActivityModalState();

  const data = useActivityModalData({
    isEditMode,
    editActivityData,
    entityType,
    entityData,
    selectedParticipants: state.selectedParticipants
  });

  const {
    participantNames,
    peopleEmails,
    resetPeopleData,
    resetParticipantNames
  } = usePeopleData(state.selectedParticipants);

  const fields = useActivityModalFields({
    activityData: data.activityData,
    isEditMode,
    entityType,
    entityData,
    participantsOptions: data?.participantsOptions,
    onParticipantsSearch: data?.onParticipantsSearch,
    isLoadingParticipants: data?.isLoadingParticipants,
    setSelectedParticipants: state?.setSelectedParticipants,
    selectedParticipants: state.selectedParticipants,
    formSetValue: state.formSetValue
  });

  const submission = useActivityModalSubmission({
    activityType,
    isEditMode,
    editActivityData,
    entityType,
    entityData,
    relatedCompanyData: data.relatedCompanyData,
    pageName,
    resetPage,
    setIsOpen
  });

  useActivityModalLifecycle({
    isOpen,
    entityType,
    entityData,
    isEditMode,
    formRef,
    formSetValue: state.formSetValue,
    resetModalState: state.resetModalState,
    resetParticipantNames,
    resetPeopleData,
    setSelectedParticipants: state.setSelectedParticipants
  });

  return {
    ...state,
    ...data,
    ...fields,
    ...submission,
    participantNames,
    peopleEmails,
    resetPeopleData,
    resetParticipantNames,
    formRef,
    effectiveCompanyId: data.effectiveCompanyId
  };
};
